import type React from "react";
import { Alert } from "@xplatform/design-system";
import { Center, Group, Loader, Stack, Text } from "@mantine/core";
import MatchTierBadge from "@components/MatchTierBadge";
import ResultCard from "@components/ResultCard";
import type { TopicTreeSearchState } from "./useTopicTreeSearch";

/** The hits of a technical-manual or wiring-diagram search. */
const TopicTreeResults: React.FC<{ state: TopicTreeSearchState }> = ({ state: { loading, error, result } }) => {
  if (loading) {
    return (
      <Center mih={160}>
        <Loader />
      </Center>
    );
  }
  if (error) {
    return (
      <Alert variant="light" color="red" title="Search failed">
        {error}
      </Alert>
    );
  }
  if (!result) return null;

  const hits = result.hits ?? [];
  if (hits.length === 0) {
    return (
      <Stack gap="sm" align="center" mih={160} justify="center">
        <Text c="dimmed">No results.</Text>
        <MatchTierBadge tier={result.matchTier} degraded={result.degraded} />
      </Stack>
    );
  }
  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="nowrap" align="center" px={4}>
        <Text size="sm" fw={500}>
          {hits.length} result{hits.length === 1 ? "" : "s"}
        </Text>
        <MatchTierBadge tier={result.matchTier} degraded={result.degraded} />
      </Group>
      {hits.map((hit, index) => (
        <ResultCard key={hit.id} hit={hit} rank={index + 1} />
      ))}
    </Stack>
  );
};

export default TopicTreeResults;
